'use client';
// ──────────────────────────────────────────────────────
// MobileQrBlock — QR entry card for mobile Q flow
//
// Renders a scannable QR code linking to /m/q on the
// current host. Designed for kiosk screens — large QR,
// clear Arabic CTA, Q branding.
// ──────────────────────────────────────────────────────
import { useEffect, useRef, useSyncExternalStore } from 'react';
import QRCode from 'qrcode';

// Stable subscriber for useSyncExternalStore (location doesn't change)
const noop = () => () => {};
function getOriginUrl() { return `${window.location.origin}/m/q`; }
function getServerUrl() { return ''; }

export function MobileQrBlock() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const url = useSyncExternalStore(noop, getOriginUrl, getServerUrl);

  // Render QR to canvas when URL is available
  useEffect(() => {
    if (canvasRef.current && url) {
      QRCode.toCanvas(canvasRef.current, url, {
        width: 180,
        margin: 2,
        color: { dark: '#1a1a2e', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      });
    }
  }, [url]);

  return (
    <div style={styles.card}>
      {/* QR Code */}
      <div style={styles.qrWrap}>
        <canvas ref={canvasRef} style={styles.canvas} />
        <div style={styles.qBadge}>Q</div>
      </div>

      {/* CTA text */}
      <p style={styles.cta}>
        امسح الكود وتكلم مع <span style={styles.ctaHighlight}>كيو</span> من جوالك
      </p>
      <p style={styles.sub}>مساعدك الذكي للعناية والجمال ✨</p>

      {/* Subtle URL hint */}
      {url && (
        <p style={styles.urlHint}>{url.replace(/^https?:\/\//, '')}</p>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f0fdfd 100%)',
    border: '1.5px solid rgba(14, 165, 165, 0.15)',
    borderRadius: 20,
    padding: '28px 24px 20px',
    textAlign: 'center',
    maxWidth: 320,
    margin: '0 auto',
    boxShadow: '0 4px 24px rgba(14, 165, 165, 0.08), 0 1px 4px rgba(0,0,0,0.04)',
  },
  qrWrap: {
    position: 'relative',
    display: 'inline-block',
    padding: 12,
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
    marginBottom: 16,
  },
  canvas: {
    display: 'block',
    borderRadius: 8,
  },
  qBadge: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #0ea5a5, #0d8f8f)',
    color: '#fff',
    fontSize: 16,
    fontWeight: 800,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(14, 165, 165, 0.3)',
  },
  cta: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1a1a2e',
    margin: '0 0 6px',
    lineHeight: 1.6,
  },
  ctaHighlight: {
    color: '#0ea5a5',
  },
  sub: {
    fontSize: 12,
    color: '#64748b',
    margin: '0 0 10px',
    lineHeight: 1.5,
  },
  urlHint: {
    fontSize: 10,
    color: '#94a3b8',
    margin: 0,
    letterSpacing: '0.02em',
    fontFamily: 'monospace',
    direction: 'ltr' as const,
  },
};
